/**
 * OpenTelemetry RUM wiring for the Observ SDK (FR-4, story 1.3).
 *
 * Sets up a {@link WebTracerProvider} that:
 *   - creates `http.client` spans for browser `fetch`/XHR (auto-instrumentation),
 *   - stamps every span with the visit's `session.id` (via {@link SessionAttributeSpanProcessor}),
 *   - injects the W3C `traceparent` header on outgoing requests (default
 *     trace-context propagator; cross-origin gated by `propagateTraceHeaderCorsUrls`),
 *   - exports spans over OTLP/HTTP to `{endpoint}/v1/traces` (BatchSpanProcessor).
 *
 * Like the rest of the SDK, this must never throw out to the host page: the
 * caller ({@link import('./index.js')}) wraps {@link setupOtelRum} defensively.
 *
 * NOTE: until the backend OTLP/HTTP receiver lands (story 1.4), exports will fail
 * at the network layer (CORS / 404). That is expected — span creation, attribute
 * stamping and header propagation are fully exercised regardless.
 */
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { registerInstrumentations, type Instrumentation } from '@opentelemetry/instrumentation'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request'
import {
  AlwaysOnSampler,
  ParentBasedSampler,
  TraceIdRatioBasedSampler,
  type Sampler,
} from '@opentelemetry/sdk-trace-base'
import {
  BatchSpanProcessor,
  WebTracerProvider,
  type ReadableSpan,
  type Span,
  type SpanProcessor,
} from '@opentelemetry/sdk-trace-web'

import { buildResource } from './resource.js'
import { SESSION_ID_ATTRIBUTE } from './constants.js'
import { getSessionId } from './session.js'
import { applyUserAttributes } from './user.js'
import type { ObservInitOptions } from './types.js'

/**
 * Head sampler from `sampleRate`: a `ParentBasedSampler(TraceIdRatioBased)` for a
 * ratio in `[0, 1)`, else `AlwaysOn` (sample everything — the default).
 */
export function buildSampler(options: ObservInitOptions): Sampler {
  const rate = options.sampleRate
  if (typeof rate === 'number' && Number.isFinite(rate) && rate >= 0 && rate < 1) {
    return new ParentBasedSampler({ root: new TraceIdRatioBasedSampler(rate) })
  }
  return new AlwaysOnSampler()
}

/**
 * URLs the auto-instrumentations must NEVER trace: the telemetry pipeline itself
 * (`{endpoint}/v1/...`). Without this the exporter's own POST gets traced,
 * spawning a self-perpetuating trickle of `http.client` spans.
 */
export function telemetryIgnoreUrls(endpoint: string): RegExp[] {
  const escaped = endpoint.replace(/\/+$/, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return [new RegExp(`${escaped}/v1/`)]
}

/**
 * Join an endpoint base URL with the OTLP/HTTP traces path. Uses the `URL` API
 * for absolute endpoints so a path prefix is preserved, a trailing slash never
 * doubles, and any query/fragment (meaningless on the traces endpoint) is
 * dropped — `https://x?a=1` ⇒ `https://x/v1/traces`, not `https://x?a=1/v1/traces`.
 * A relative or empty endpoint falls back to a string join, yielding a
 * same-origin `…/v1/traces` path.
 */
export function buildTracesUrl(endpoint: string): string {
  try {
    const url = new URL(endpoint)
    url.pathname = `${url.pathname.replace(/\/+$/, '')}/v1/traces`
    url.search = ''
    url.hash = ''
    return url.toString()
  } catch {
    return `${endpoint.replace(/\/+$/, '')}/v1/traces`
  }
}

/**
 * Span processor whose only job is to stamp `session.id` on every span at start.
 *
 * Using a processor (rather than per-instrumentation `applyCustomAttributesOnSpan`)
 * guarantees ALL spans — fetch, XHR, and any future document-load — carry the
 * attribute through a single code path. It reads the id via {@link getSessionId},
 * which also refreshes the session's inactivity window (story 1.2 decision): a
 * span therefore counts as session activity.
 */
export class SessionAttributeSpanProcessor implements SpanProcessor {
  onStart(span: Span): void {
    // onStart runs synchronously inside the host page's own fetch/XHR. The SDK
    // must never throw out to the page, so guard here even though getSessionId()
    // is contractually no-throw: a missing attribute beats a broken host request.
    try {
      span.setAttribute(SESSION_ID_ATTRIBUTE, getSessionId())
    } catch {
      /* swallow — never let span stamping break a host request */
    }
  }
  onEnd(_span: ReadableSpan): void {}
  forceFlush(): Promise<void> {
    return Promise.resolve()
  }
  shutdown(): Promise<void> {
    return Promise.resolve()
  }
}

/**
 * Span processor that stamps the end-user identity (`enduser.pseudo.id`, and
 * `enduser.id`/role/name once {@link import('./user.js').setUser} was called) on
 * every span at start — the trace mirror of how {@link import('./otel-logs.js')}
 * stamps logs. Kept separate from {@link SessionAttributeSpanProcessor} so each
 * concern stays single-purpose; both run before the batch/export processor.
 */
export class UserAttributeSpanProcessor implements SpanProcessor {
  onStart(span: Span): void {
    // Runs synchronously inside the host's fetch/XHR; applyUserAttributes is itself
    // guarded, but a span without enduser attrs beats a broken host request.
    applyUserAttributes((key, value) => span.setAttribute(key, value))
  }
  onEnd(_span: ReadableSpan): void {}
  forceFlush(): Promise<void> {
    return Promise.resolve()
  }
  shutdown(): Promise<void> {
    return Promise.resolve()
  }
}

let provider: WebTracerProvider | null = null
let disableInstrumentations: (() => void) | null = null
let activeConfig: { endpoint: string; key: string } | null = null
/** Resolves once the opt-in `userInteraction` async setup has attached (or null
 *  when not opted in). Test-only readers use {@link getUserInteractionReady}. */
let userInteractionReady: Promise<void> | null = null

/**
 * The always-on web auto-instrumentations: page-load timing + fetch/XHR with W3C
 * `traceparent` propagation. Built here (no zone.js) and shared by both the
 * default registration and the opt-in user-interaction path.
 */
function buildBaseInstrumentations(
  options: ObservInitOptions,
  ignoreUrls: RegExp[],
): Instrumentation[] {
  return [
    // Page-load timing: documentLoad / documentFetch / resourceFetch spans.
    // applyCustomAttributesOnSpan adds flat numeric duration attrs for the network
    // phases so they are queryable via map_extract(span_attrs, 'network.*').
    // The DocumentLoadInstrumentation adds these phases as span *events* only;
    // flat attrs are needed for DataFusion cohort queries.
    new DocumentLoadInstrumentation({
      applyCustomAttributesOnSpan: {
        documentLoad: (span) => {
          try {
            const nav = performance.getEntriesByType('navigation')[0] as
              PerformanceNavigationTiming | undefined
            if (!nav) return
            const dns = nav.domainLookupEnd - nav.domainLookupStart
            const connect = nav.connectEnd - nav.connectStart
            const ttfb = nav.responseStart - nav.requestStart
            if (dns >= 0) span.setAttribute('network.dns_ms', dns)
            if (connect >= 0) span.setAttribute('network.connect_ms', connect)
            // Only emit tls_ms on HTTPS: secureConnectionStart === 0 means HTTP (no TLS).
            if (nav.secureConnectionStart > 0) {
              span.setAttribute('network.tls_ms', nav.connectEnd - nav.secureConnectionStart)
            }
            if (ttfb >= 0) span.setAttribute('network.ttfb_ms', ttfb)
          } catch {
            /* swallow — attribute stamping must never break the host page */
          }
        },
      },
    }),
    new FetchInstrumentation({
      propagateTraceHeaderCorsUrls: options.propagateTraceHeaderCorsUrls,
      ignoreUrls,
    }),
    new XMLHttpRequestInstrumentation({
      propagateTraceHeaderCorsUrls: options.propagateTraceHeaderCorsUrls,
      ignoreUrls,
    }),
  ]
}

/**
 * Wire up OTel RUM tracing. Idempotent: a second call is a no-op while a provider
 * is already active (mirrors the single-`init` contract; first call wins). Not
 * defensive on its own — `init` owns the try/catch so a setup failure never
 * escapes to the page — but it cleans up after itself: a partial init (register
 * or instrumentation throwing, e.g. SSR / no `fetch`/XHR) shuts the provider down
 * and resets the guard before rethrowing, so no `WebTracerProvider`
 * (BatchSpanProcessor timer + global registration) leaks and a later retry works.
 */
export function setupOtelRum(options: ObservInitOptions): void {
  if (provider) {
    // First init wins. Warn if a later call diverges (key rotation / env switch)
    // so the dropped config isn't silently swallowed by the idempotence guard.
    if (
      activeConfig &&
      (activeConfig.endpoint !== options.endpoint || activeConfig.key !== options.key)
    ) {
      console.warn(
        '[observ] OTel RUM already initialized; ignoring divergent endpoint/key on this init() (first call wins).',
      )
    }
    return
  }

  // API-key auth via the custom `x-observ-key` header — the receiver (story 1.4)
  // must decode the same name and list it in `Access-Control-Allow-Headers`. An
  // empty key would produce a malformed credential, so omit the header (and warn)
  // rather than send a blank one.
  const headers: Record<string, string> = {}
  if (options.key) {
    headers['x-observ-key'] = options.key
  } else {
    console.warn('[observ] no API key provided; front-end spans will export unauthenticated.')
  }

  const exporter = new OTLPTraceExporter({
    url: buildTracesUrl(options.endpoint),
    headers,
  })

  const ignoreUrls = telemetryIgnoreUrls(options.endpoint)

  // SDK 2.x: span processors are constructor-only (`addSpanProcessor` was removed).
  // Order matters — stamp `session.id` before the batch processor exports.
  const p = new WebTracerProvider({
    resource: buildResource(options),
    sampler: buildSampler(options),
    spanProcessors: [
      new SessionAttributeSpanProcessor(),
      new UserAttributeSpanProcessor(),
      new BatchSpanProcessor(exporter),
    ],
  })

  const base = buildBaseInstrumentations(options, ignoreUrls)

  if (options.userInteraction) {
    // Opt-in: user-interaction spans need a ZoneContextManager to keep trace
    // context across the interaction's async work. Both pull in zone.js, which
    // monkey-patches the host's global timers/Promise AT IMPORT — so they live in
    // a separate module loaded ONLY here, via dynamic import, keeping the default
    // bundle (and the host globals) untouched for everyone who doesn't opt in.
    // The provider is committed synchronously (idempotence); the ZoneContextManager
    // registration + instrumentation attach one microtask later.
    provider = p
    activeConfig = { endpoint: options.endpoint, key: options.key }
    userInteractionReady = import('./user-interaction.js')
      .then(({ setupUserInteraction }) => {
        // A concurrent shutdown may have cleared the provider before we resolved.
        if (provider !== p) return
        disableInstrumentations = setupUserInteraction(p, base)
      })
      .catch((err) => {
        console.warn('[observ] user-interaction setup failed; tracing degraded.', err)
        if (provider === p) {
          provider = null
          activeConfig = null
          void p.shutdown()
        }
      })
    return
  }

  try {
    // Default registration: W3C trace-context propagator + web StackContextManager.
    p.register()
    disableInstrumentations = registerInstrumentations({
      tracerProvider: p,
      instrumentations: base,
    })
  } catch (err) {
    // Roll back a partial init so nothing leaks and the guard doesn't latch.
    disableInstrumentations?.()
    disableInstrumentations = null
    void p.shutdown()
    throw err
  }

  provider = p
  activeConfig = { endpoint: options.endpoint, key: options.key }
}

/**
 * @internal Test-only. The promise that resolves once the opt-in
 * `userInteraction` async setup has attached, or `null` when not opted in (proving
 * the default path stays synchronous and never loads zone.js).
 */
export function getUserInteractionReady(): Promise<void> | null {
  return userInteractionReady
}

/**
 * @internal Test-only. The active provider, or `null` when tracing is not set
 * up. Lets tests assert idempotence (same instance after a second `setupOtelRum`).
 */
export function getActiveProvider(): WebTracerProvider | null {
  return provider
}

/**
 * @internal Tear down instrumentations and the provider, resetting the
 * idempotence guard. Used by tests (and any future explicit teardown). Not on
 * the public {@link import('./types.js').ObservSdk} surface.
 */
export async function shutdownOtelRum(): Promise<void> {
  // Let any in-flight opt-in user-interaction setup settle first, so its async
  // `registerInstrumentations` can't attach after we've torn the provider down.
  const pending = userInteractionReady
  userInteractionReady = null
  if (pending) {
    try {
      await pending
    } catch {
      /* the setup already logged + rolled back */
    }
  }
  const p = provider
  // Atomic: even if disabling instrumentations throws, still null the guard and
  // shut the provider down so the BatchSpanProcessor timer can't outlive teardown.
  try {
    disableInstrumentations?.()
  } finally {
    disableInstrumentations = null
    provider = null
    activeConfig = null
    await p?.shutdown()
  }
}
